import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Input } from "@airhelp/react";
import { Box, Button, Heading, VStack } from "@chakra-ui/react";

import { ErrorBadge } from "elements";
import UserConsentsForm from "elements/UserConsentsForm";
import useEffectOnce from "hooks/useEffectOnce";
import { ISignupSubmitData } from "models/User";
import { useTrackEvent } from "utils/tracking/hooks";
import { minPasswordLength } from "utils/validations";

export type ActivationSubmitData = Omit<ISignupSubmitData, "email">;

export interface IPageContentComponentParams {
  isSubmitting: boolean;
  isSubmitError: boolean;
  email: string;
  onSubmit: (submitData: ActivationSubmitData) => void;
}

const PageContentComponent: React.FC<IPageContentComponentParams> = ({
  isSubmitting,
  isSubmitError,
  email,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { trackOnboardingFunnel, trackCtaClick } = useTrackEvent();

  const passwordConfirmationErrMsg = t("errors.passwords_do_not_match") as string;

  const form = useForm<ISignupSubmitData>({ mode: "onChange" });
  const { handleSubmit, control, formState, watch } = form;
  const { isValid, errors } = formState;

  const activateTrip = (data: ActivationSubmitData) => {
    const { password, passwordConfirmation, privacyConsent, marketingConsent } = data;
    const userData = {
      email,
      password,
      passwordConfirmation,
      privacyConsent,
      marketingConsent,
    };
    onSubmit(userData);
  };

  useEffectOnce(() => {
    trackOnboardingFunnel("partner user onboarding with password setup");
  });

  return (
    <Box width="100%">
      <Heading variant="h1" my={8} data-testid="trip-registration-header">
        {t("authentication.signup_form.finish_registration")}
      </Heading>
      <form onSubmit={handleSubmit(activateTrip)}>
        <VStack spacing={6} mb={6}>
          <Box w="100%">
            <Input
              placeholder={email}
              size="lg"
              label={t("authentication.email_address")}
              type="email"
              id="email"
              name="email"
              disabled
              data-testid="email"
            />
          </Box>
          <Controller
            render={({ field: { onChange, name } }) => (
              <Box w="100%">
                <Input
                  isInvalid={!!errors.password}
                  errorMessage={errors.password?.message || ""}
                  placeholder="********"
                  size="lg"
                  label={t("authentication.password_form.set_password")}
                  name={name}
                  type="password"
                  id="password"
                  autoComplete="new-password-confirmation"
                  onChange={onChange}
                  data-testid="password"
                />
              </Box>
            )}
            name="password"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              minLength: {
                value: minPasswordLength,
                message: t("errors.too_short", { min: minPasswordLength, attribute: t("authentication.password") }),
              },
            }}
            defaultValue=""
          />
          <Controller
            render={({ field: { onChange, name } }) => (
              <Box w="100%">
                <Input
                  isInvalid={!!errors.passwordConfirmation}
                  errorMessage={errors.passwordConfirmation?.message || ""}
                  placeholder="********"
                  size="lg"
                  label={t("authentication.password_form.repeat_password")}
                  name={name}
                  type="password"
                  id="password_confirmation"
                  autoComplete="new-password"
                  onChange={onChange}
                  data-testid="password-confirmation"
                />
              </Box>
            )}
            name="passwordConfirmation"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              validate: (value) => value === watch("password") || passwordConfirmationErrMsg,
            }}
            defaultValue=""
          />
          <UserConsentsForm form={form} requireTerms requireMarketing />
        </VStack>
        <Button
          size="s"
          width="100%"
          onClick={() => {
            trackCtaClick("finish", "finish registration page");
            handleSubmit(activateTrip)();
          }}
          isDisabled={!isValid || isSubmitting}
          isLoading={isSubmitting}
          data-testid="registration-finish"
        >
          {t("authentication.signup_form.finish")}
        </Button>
        {isSubmitError ? (
          <Box my={2}>
            <ErrorBadge />
          </Box>
        ) : null}
      </form>
    </Box>
  );
};

export default PageContentComponent;
